import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
  Legend,
} from "recharts";
import {
  Camera,
  CreditCard,
  PackageCheck,
  Truck,
  ArrowRight,
  CheckCircle2,
  Inbox,
  Send,
  Package,
  ShoppingBag,
  DollarSign,
  Star,
  ShieldCheck,
} from "lucide-react";
import { AppShell } from "@/components/AppShell";
import { getDashboardOverview } from "@/lib/dashboard-overview.functions";
import {
  getPriorityActions,
  getMonthlyActivity,
  type PriorityAction,
  type ActionKind,
} from "@/lib/priority-actions.functions";
import { ProfileCompletionBanner } from "@/components/ProfileCompletionBanner";
import { TwoFactorPromptBanner } from "@/components/TwoFactorPromptBanner";
import { useCurrency } from "@/lib/currency";

function DashboardError({ error, reset }: { error: Error; reset: () => void }) {
  return (
    <AppShell>
      <div className="panel p-8">
        <h1 className="font-display text-xl font-bold">Dashboard failed to load</h1>
        <p className="mt-2 text-sm text-destructive whitespace-pre-wrap break-words">
          {error?.message ?? String(error)}
        </p>
        <button onClick={reset} className="btn-primary mt-4 px-4 py-2 text-sm">
          Retry
        </button>
      </div>
    </AppShell>
  );
}

export const Route = createFileRoute("/_authenticated/dashboard")({
  head: () => ({ meta: [{ title: "Dashboard — Want To Buy" }] }),
  component: Dashboard,
  errorComponent: DashboardError,
  notFoundComponent: () => (
    <AppShell>
      <div className="panel p-8 text-sm">Not found.</div>
    </AppShell>
  ),
});

const ACTION_META: Record<
  ActionKind,
  { label: string; icon: typeof Camera; button: string; side: "buying" | "selling" }
> = {
  review_photos: { label: "Photos awaiting your review", icon: Camera, button: "Approve / Reject", side: "buying" },
  pay_now: { label: "Payment pending", icon: CreditCard, button: "Pay Now", side: "buying" },
  confirm_receipt: { label: "Delivery — confirm receipt", icon: PackageCheck, button: "Confirm Receipt", side: "buying" },
  submit_photos: { label: "Photos required for accepted offer", icon: Camera, button: "Submit Photos", side: "selling" },
  mark_shipped: { label: "Payment confirmed — ship now", icon: Truck, button: "Mark Shipped", side: "selling" },
};

function useFmtMoney() {
  const { display, format } = useCurrency();
  return (cents: number | null | undefined) => {
    const usd = (cents ?? 0) / 100;
    const base = `$${usd.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
    return display === "USD" ? base : `${base} · ≈ ${format(usd)}`;
  };
}

function timeLeft(iso: string | null): string {
  if (!iso) return "";
  const diff = new Date(iso).getTime() - Date.now();
  if (diff <= 0) return "Overdue";
  const h = Math.floor(diff / 3_600_000);
  if (h < 1) return `${Math.max(1, Math.floor(diff / 60_000))}m`;
  if (h < 48) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
}

function PriorityRow({ a }: { a: PriorityAction }) {
  const meta = ACTION_META[a.kind];
  const Icon = meta.icon;
  const t = timeLeft(a.deadline_at);
  const overdue = t === "Overdue";
  const fmtMoney = useFmtMoney();
  return (
    <div className="flex items-center gap-3 border-b border-border px-4 py-3 last:border-0">
      <span className="grid h-9 w-9 shrink-0 place-items-center rounded-md border border-border bg-panel">
        <Icon className="h-4 w-4" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-semibold">{a.card_name}</div>
        <div className="mt-0.5 truncate text-xs text-muted-foreground">
          {a.counterparty_username ? `@${a.counterparty_username} · ` : ""}
          {meta.label}
          {a.amount_cents != null ? ` · ${fmtMoney(a.amount_cents)}` : ""}
        </div>
      </div>
      {t && (
        <span className={`hidden shrink-0 text-xs sm:block ${overdue ? "text-destructive font-semibold" : "text-muted-foreground"}`}>
          {overdue ? "Overdue" : `${t} left`}
        </span>
      )}
      {a.detail_kind === "order" ? (
        <Link to="/orders/$id" params={{ id: a.detail_id }} className="btn-primary shrink-0 px-3 py-1.5 text-xs">
          {meta.button}
        </Link>
      ) : a.detail_kind === "checkout" ? (
        <Link to="/orders/$id" params={{ id: a.detail_id }} className="btn-primary shrink-0 px-3 py-1.5 text-xs">
          {meta.button}
        </Link>
      ) : (
        <Link to="/transaction/$id" params={{ id: a.detail_id }} className="btn-primary shrink-0 px-3 py-1.5 text-xs">
          {meta.button}
        </Link>
      )}
    </div>
  );
}

function StatChip({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string | number;
}) {
  return (
    <div className="flex items-center gap-2 rounded-md border border-border bg-panel/60 px-3 py-2">
      <span className="grid h-7 w-7 shrink-0 place-items-center rounded-md bg-muted text-muted-foreground">
        {icon}
      </span>
      <div className="min-w-0">
        <div className="truncate text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
        <div className="font-display text-base font-bold leading-tight">{value}</div>
      </div>
    </div>
  );
}

function Dashboard() {
  const fetchOverview = useServerFn(getDashboardOverview);
  const fetchActions = useServerFn(getPriorityActions);
  const fetchActivity = useServerFn(getMonthlyActivity);
  const fmtMoney = useFmtMoney();
  const { format: fmtCurrency } = useCurrency();

  const overviewQ = useQuery({ queryKey: ["dashboard-overview"], queryFn: () => fetchOverview() });
  const actionsQ = useQuery({
    queryKey: ["priority-actions", "dashboard"],
    queryFn: () => fetchActions(),
    refetchInterval: 30_000,
  });
  const activityQ = useQuery({ queryKey: ["monthly-activity"], queryFn: () => fetchActivity() });

  const overview = overviewQ.data;
  const actions = actionsQ.data;
  const activity = activityQ.data;

  const buyingActions = actions?.buying ?? [];
  const sellingActions = actions?.selling ?? [];
  const total = (actions?.total ?? 0);

  const profile = overview?.profile;
  const greetingName = profile?.display_name || profile?.username || "there";

  const trusted = !!profile?.trusted_seller_since;
  const verifiedStore = !!profile?.verified_store;

  const isLoading = overviewQ.isLoading || actionsQ.isLoading;

  return (
    <AppShell>
      <ProfileCompletionBanner />
      <TwoFactorPromptBanner transactions={0} payouts={0} />

      {/* Greeting */}
      <div className="mb-6">
        <h1 className="font-display text-3xl font-bold">Welcome back, {greetingName}</h1>
        <p className="text-sm text-muted-foreground">
          {total > 0
            ? `You have ${total} pending action${total === 1 ? "" : "s"} needing your attention.`
            : "Account in good standing. No pending actions."}
          {trusted ? " · Trusted Seller." : ""}
          {verifiedStore ? " · Verified Store." : ""}
        </p>
      </div>

      {isLoading && !overview && (
        <div className="panel p-10 text-center text-sm text-muted-foreground">Loading your dashboard…</div>
      )}

      {overview && (
        <>
          {/* Stat strips */}
          <div className="mb-6 grid gap-4 lg:grid-cols-2">
            <div className="panel p-4">
              <div className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                <ShoppingBag className="h-3.5 w-3.5" /> Buying activity
              </div>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                <StatChip icon={<Inbox className="h-3.5 w-3.5" />} label="Active orders" value={overview.counts.buying.active} />
                <StatChip icon={<Send className="h-3.5 w-3.5" />} label="Offers received" value={overview.counts.buying.offers_received} />
                <StatChip icon={<CheckCircle2 className="h-3.5 w-3.5" />} label="Pending confirmation" value={overview.counts.buying.pending_confirmation} />
                <StatChip icon={<Truck className="h-3.5 w-3.5" />} label="In transit" value={overview.counts.buying.in_transit} />
              </div>
            </div>

            <div className="panel p-4">
              <div className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                <DollarSign className="h-3.5 w-3.5" /> Selling activity
              </div>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
                <StatChip icon={<Send className="h-3.5 w-3.5" />} label="Submitted" value={overview.counts.selling.offers_submitted} />
                <StatChip icon={<CheckCircle2 className="h-3.5 w-3.5" />} label="Accepted" value={overview.counts.selling.offers_accepted} />
                <StatChip icon={<Camera className="h-3.5 w-3.5" />} label="Photos req." value={overview.counts.selling.photos_required} />
                <StatChip icon={<Package className="h-3.5 w-3.5" />} label="To ship" value={overview.counts.selling.items_to_ship} />
                <StatChip icon={<CheckCircle2 className="h-3.5 w-3.5" />} label="Pending confirmation" value={overview.counts.selling.pending_confirmation} />
                <StatChip icon={<Star className="h-3.5 w-3.5" />} label="Completed" value={overview.counts.selling.completed_sales} />
              </div>
            </div>
          </div>

          {/* Priority Actions */}
          <section className="mb-6">
            <div className="mb-3 flex items-end justify-between">
              <h2 className="font-display text-lg font-bold">Priority Actions</h2>
              <span className="text-xs text-muted-foreground">{total} total</span>
            </div>
            {total === 0 ? (
              <div className="panel flex flex-col items-center gap-2 p-10 text-center">
                <CheckCircle2 className="h-8 w-8 text-emerald-500" />
                <div className="text-sm font-semibold">All caught up.</div>
                <p className="text-xs text-muted-foreground">Nothing needs your attention right now.</p>
              </div>
            ) : (
              <div className="grid gap-4 lg:grid-cols-2">
                <div className="panel overflow-hidden">
                  <div className="flex items-center justify-between border-b border-border px-4 py-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    <span>Buying</span>
                    <span>{buyingActions.length}</span>
                  </div>
                  {buyingActions.length === 0 ? (
                    <div className="p-6 text-center text-xs text-muted-foreground">No buyer actions pending.</div>
                  ) : (
                    buyingActions.map((a) => <PriorityRow key={a.id} a={a} />)
                  )}
                </div>
                <div className="panel overflow-hidden">
                  <div className="flex items-center justify-between border-b border-border px-4 py-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    <span>Selling</span>
                    <span>{sellingActions.length}</span>
                  </div>
                  {sellingActions.length === 0 ? (
                    <div className="p-6 text-center text-xs text-muted-foreground">No seller actions pending.</div>
                  ) : (
                    sellingActions.map((a) => <PriorityRow key={a.id} a={a} />)
                  )}
                </div>
              </div>
            )}
          </section>

          {/* Two side-by-side tables */}
          <section className="mb-6 grid gap-4 lg:grid-cols-2">
            <div className="panel overflow-hidden">
              <div className="flex items-center justify-between border-b border-border px-4 py-3">
                <h3 className="font-display text-sm font-bold">Recent Buy Orders</h3>
                <Link to="/create-order" className="text-xs text-primary hover:underline">+ New</Link>
              </div>
              {overview.recentBuyOrders.length === 0 ? (
                <div className="p-10 text-center text-sm text-muted-foreground">
                  You haven't posted any buy orders yet.{" "}
                  <Link to="/create-order" className="text-primary hover:underline">Post your first order</Link>.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="text-[10px] uppercase tracking-wider text-muted-foreground">
                      <tr className="border-b border-border">
                        <th className="px-3 py-2 text-left font-semibold">Card</th>
                        <th className="px-3 py-2 text-left font-semibold">Cond.</th>
                        <th className="px-3 py-2 text-right font-semibold">Target</th>
                        <th className="px-3 py-2 text-right font-semibold">Offers</th>
                        <th className="px-3 py-2 text-left font-semibold">Status</th>
                        <th className="px-3 py-2 text-right font-semibold">Updated</th>
                      </tr>
                    </thead>
                    <tbody>
                      {overview.recentBuyOrders.map((o: any) => (
                        <tr key={o.id} className="border-b border-border last:border-0 hover:bg-muted/30">
                          <td className="px-3 py-2">
                            <Link to="/orders/$id" params={{ id: o.id }} className="font-semibold hover:underline">
                              {o.title || o.card_name}
                            </Link>
                            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{o.tcg}</div>
                          </td>
                          <td className="px-3 py-2 text-xs text-muted-foreground">{o.condition_min ?? "—"}</td>
                          <td className="px-3 py-2 text-right font-semibold">{fmtMoney(o.max_price_cents)}</td>
                          <td className="px-3 py-2 text-right">{o.offers_received}</td>
                          <td className="px-3 py-2 text-xs">{o.status}</td>
                          <td className="px-3 py-2 text-right text-[11px] text-muted-foreground">
                            {new Date(o.updated_at).toLocaleDateString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="panel overflow-hidden">
              <div className="flex items-center justify-between border-b border-border px-4 py-3">
                <h3 className="font-display text-sm font-bold">Recent Sale Offers</h3>
                <Link to="/" className="text-xs text-primary hover:underline">Browse orders</Link>
              </div>
              {overview.recentSaleOffers.length === 0 ? (
                <div className="p-10 text-center text-sm text-muted-foreground">
                  You haven't submitted any offers yet.{" "}
                  <Link to="/" className="text-primary hover:underline">Find buyers</Link>.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="text-[10px] uppercase tracking-wider text-muted-foreground">
                      <tr className="border-b border-border">
                        <th className="px-3 py-2 text-left font-semibold">Card</th>
                        <th className="px-3 py-2 text-right font-semibold">Buyer target</th>
                        <th className="px-3 py-2 text-right font-semibold">Your offer</th>
                        <th className="px-3 py-2 text-left font-semibold">Status</th>
                        <th className="px-3 py-2 text-right font-semibold">Updated</th>
                      </tr>
                    </thead>
                    <tbody>
                      {overview.recentSaleOffers.map((o: any) => (
                        <tr key={o.id} className="border-b border-border last:border-0 hover:bg-muted/30">
                          <td className="px-3 py-2">
                            <Link to="/orders/$id" params={{ id: o.buy_order_id }} className="font-semibold hover:underline">
                              {o.card_name}
                            </Link>
                          </td>
                          <td className="px-3 py-2 text-right text-muted-foreground">
                            {o.buyer_target_cents != null ? fmtMoney(o.buyer_target_cents) : "—"}
                          </td>
                          <td className="px-3 py-2 text-right font-semibold">{fmtMoney(o.price_cents)}</td>
                          <td className="px-3 py-2 text-xs">{o.status}</td>
                          <td className="px-3 py-2 text-right text-[11px] text-muted-foreground">
                            {new Date(o.updated_at).toLocaleDateString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </section>

          {/* Activity chart */}
          <section className="mb-6">
            <div className="panel p-4">
              <div className="mb-2 flex items-center justify-between">
                <h3 className="font-display text-sm font-bold">Platform Activity — Last 12 Months</h3>
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Buying vs Selling</span>
              </div>
              {activity && activity.months.some((m) => m.buying_cents || m.selling_cents) ? (
                <div className="h-64 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                      data={activity.months.map((m) => ({
                        label: m.label,
                        Buying: m.buying_cents / 100,
                        Selling: m.selling_cents / 100,
                      }))}
                      margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis dataKey="label" stroke="hsl(var(--muted-foreground))" fontSize={11} />
                      <YAxis
                        stroke="hsl(var(--muted-foreground))"
                        fontSize={11}
                        tickFormatter={(v) => `$${v}`}
                      />
                      <Tooltip
                        contentStyle={{
                          background: "hsl(var(--panel))",
                          border: "1px solid hsl(var(--border))",
                          borderRadius: 8,
                          fontSize: 12,
                        }}
                        formatter={(v: number) => fmtCurrency(v)}
                      />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      <Line type="monotone" dataKey="Buying" stroke="hsl(var(--primary))" strokeWidth={2} dot={false} />
                      <Line type="monotone" dataKey="Selling" stroke="hsl(var(--chart-2, 142 76% 36%))" strokeWidth={2} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="p-10 text-center text-sm text-muted-foreground">
                  No completed transactions yet. Your monthly buying spend and selling earnings will appear here.
                </div>
              )}
            </div>
          </section>

          {/* Footer cards */}
          <section className="grid gap-4 sm:grid-cols-3">
            <div className="panel p-4">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                <Star className="h-3.5 w-3.5" /> Trusted Seller
              </div>
              {trusted ? (
                <>
                  <div className="mt-2 font-display text-base font-bold text-emerald-500">Active</div>
                  <p className="mt-1 text-xs text-muted-foreground">
                    You've been a Trusted Seller since {new Date(profile.trusted_seller_since).toLocaleDateString()}.
                  </p>
                </>
              ) : (
                <>
                  <div className="mt-2 font-display text-base font-bold">In progress</div>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Complete more successful sales to unlock the Trusted Seller badge.
                  </p>
                  <Link to="/seller-hub" className="mt-3 inline-block text-xs text-primary hover:underline">
                    View Seller Hub →
                  </Link>
                </>
              )}
            </div>

            <div className="panel p-4">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                <ShieldCheck className="h-3.5 w-3.5" /> Verified Store
              </div>
              {verifiedStore ? (
                <>
                  <div className="mt-2 font-display text-base font-bold text-emerald-500">Verified</div>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {profile.store_name ?? "Your store"} is a Verified Store.
                  </p>
                </>
              ) : (
                <>
                  <div className="mt-2 font-display text-base font-bold">Not verified</div>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Apply for Verified Store status to unlock a premium buyer badge.
                  </p>
                  <Link to="/seller-verification" className="mt-3 inline-block text-xs text-primary hover:underline">
                    Apply now →
                  </Link>
                </>
              )}
            </div>

            <div className="panel p-4">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                <ArrowRight className="h-3.5 w-3.5" /> Account
              </div>
              <div className="mt-2 font-display text-base font-bold">Settings & security</div>
              <p className="mt-1 text-xs text-muted-foreground">
                Manage your profile, notifications, and 2FA.
              </p>
              <Link to="/account" className="mt-3 inline-block text-xs text-primary hover:underline">
                Go to Account Settings →
              </Link>
            </div>
          </section>
        </>
      )}
    </AppShell>
  );
}
